// Core lib dependencies.
import _forEach from 'lodash/forEach';
import _extend from 'lodash/extend';
import _includes from 'lodash/includes';
import _get from 'lodash/get';
import htmlTruncate from 'html-truncate';
import util from 'util'; // node util
import TwitterText from 'twitter-text';

const INSTAGRAM_TAG_URL_PREFIX = 'https://www.instagram.com/explore/tags/';
const TWITTER_TAG_URL_PREFIX = 'https://twitter.com/hashtag/';
const FACEBOOK_TAG_URL_PREFIX = 'https://www.facebook.com/hashtag/';
const TWITTER_SCREEN_NAME_URL_PREFIX = 'https://twitter.com/';
const INSTAGRAM_SCREEN_NAME_URL_PREFIX = 'https://instagram.com/';

/**
 * <pre>
 * String utility functions.
 *
 * Summary of responsibilities:
 *
 *  - provides commonly used functionality that can be called from anywhere.
 * </pre>
 *
 * @author Various
 * @namespace Common.Utils.String
 */

/**
 * Capitalizes a string and lowercases the remainder of the string.
 *
 * @memberOf Common.Utils.String
 *
 * @param {string} str - String to capitalize
 * @returns {string|boolean}
 */
function capitalize(str) {
  if (!str) return false;

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
export { capitalize };

/**
 * Capitalizes a string, but leaves the remainder of string case intact.
 *
 * @memberOf Common.Utils.String
 *
 * @param {string} str - String to capitalize
 * @returns {string|boolean}
 */
function UCFirst(str) {
  if (!str) return false;

  return str.charAt(0).toUpperCase() + str.slice(1);
}
export { UCFirst };

/**
 *
 * Converts the string into camelCase.
 * Example: This is an Example String => thisIsAnExampleString
 * @param {string} str String to convert (words are separated with WHITESPACES!)
 * @returns {string}
 */
function camelCase(str) {
  return str
    .split(' ')
    .map((item, idx) => (idx === 0 ? item.toLowerCase() : UCFirst(item)))
    .join('');
}
export { camelCase };

/**
 * Returns string safe for React to render
 *
 * @memberOf Common.Utils.String
 *
 * @param content : {string}
 * @returns {object} : HTML sanitized for React
 */
function sanitizeHTML(content) {
  return { __html: content };
}
export { sanitizeHTML };

/**
 * Attempt to retrieve a label string from legacy NGX.lang namespace.
 * [TL] - this will hopefully be replaced by a different i18n system at some point this year.
 *
 * @memberOf Common.Utils.String
 *
 * @param  labelPath: {string} The label path.
 * @return {string}
 */
function getLabel(labelPath, fallback) {
  if (typeof NGX === 'undefined') {
    return fallback;
  }
  return NGX.lang.get(labelPath) || fallback;
}

export { getLabel };

/**
 * String token replacement. add more if required. call with an object's scope as a fallback,
 * or pass object as a param. cannot be called without a parameter & using "window" as scope,
 * because that would be stupid.
 *
 * @memberOf Common.Utils.String
 *
 * @param str : {string} The template string.
 * @param obj : {object} The object to search inside for replacement values.
 * @returns {string}
 */
function tokenize(str, obj) {
  let match,
    // get the correct context
    context = obj || (this === window ? {} : this);

  if (typeof str === 'string') {
    // match any grails-style tokens: ${tokenName}
    while ((match = /\$\{(.*?)\}/g.exec(str))) {
      // replace the token with the matching value from the scoped object, using parenthesised substring from returned array & trim . eg. gets 'a' from '${ a }'
      str = str.replace(match[0], context[trim(match[1])] || '');
    }
    return str;
  }

  // don't break callers without type checks on the returned value.
  return '';
}
export { tokenize };

/**
 * Yet another String trim (added to remove a jQuery dependency)
 * NOTE: Wrapped as a polyfill, the method returns native trim function if available
 *
 * @memberOf Common.Utils.String
 *
 * @param {string} str : string to trim
 * @returns {string}
 */
const trim = (function (nativeTrimSupport) {
  // wrap the native trim if present
  if (nativeTrimSupport)
    return function nativeTrim(str) {
      return 'string' === typeof str ? str.trim(str) : false;
    };
  // otherwise use the fastest trim we can come up with
  return function fastTrim(str) {
    if ('string' !== typeof str) return false;
    const len = str.length,
      checkCode = function (code) {
        const c = str.charCodeAt(code);
        return c != 32 && c != 10 && c != 13 && c != 9 && c != 12;
      };
    for (var i = 0; i < len; i++) {
      if (checkCode(i)) break;
    }
    for (var j = len - 1; j >= i; j--) {
      if (checkCode(j)) break;
    }
    return str.substring(i, j + 1);
  };
})('function' === typeof String.prototype.trim);
export { trim };

/**
 * Translates a link string to link + action string where actionRef is passed
 *
 * @memberOf Common.Utils.String
 *
 * @param link: {string} link or actionRef
 * @returns {object} with href + data action
 */
function translateHref(link) {
  if (!link) return;
  const _linkParts = link.split(':');
  const translated = {
    href:   link,
    action: null,
  };

  if (_linkParts.length == 1 || _linkParts[0].indexOf('http') === 0) return translated;

  translated.href = '#';
  if (_linkParts[0] === 'action') {
    translated.action = _linkParts.slice(1).join(':');
  } else {
    translated.action = _linkParts.join(':');
  }

  return translated;
}
export { translateHref };

/**
 * Adds items to current page's query string, or on fullUrl if given.
 *
 * @memberOf Common.Utils.String
 *
 * @param  newParams: 		{object}	The object of params to add/replace
 * @param  replaceIfExists: {object}	Replace existing url params?
 * @param  fullUrl: 		{object}	Arbitrary full url
 * @returns {string}
 */
function updateQueryString(newParams, replaceIfExists, fullUrl) {
  let _newParams = newParams || {},
    _replace = !!replaceIfExists === false ? false : true,
    _qUrl = (fullUrl === 'ctx:smarturl' ? NGX.context.smarturl : fullUrl) || document.location.href,
    _hasQS = _qUrl.indexOf('?') > -1 ? true : false,
    _qString = _hasQS ? _qUrl.substring(_qUrl.indexOf('?') + 1) : '',
    _qSObj = {},
    _newQSObj = {};

  if (_qString) {
    _forEach(_qString.split('&'), function (item) {
      const kvp = item.split('=');
      _qSObj[kvp[0]] = kvp[1] || undefined;
    });
  }

  if (_replace) {
    _extend(_newQSObj, _qSObj, _newParams);
  } else {
    _extend(_newQSObj, _newParams, _qSObj);
  }

  if (_hasQS) _qUrl = _qUrl.substring(0, _qUrl.indexOf('?'));

  return _qUrl + '?' + decodeURIComponent(param(_newQSObj));
}
export { updateQueryString };

/**
 * Convert a url encoded query string from a shallow object
 *
 * @memberOf Common.Utils.String
 *
 * @param {object} params
 * @returns {string}
 */
function param(params) {
  let _encodedStr = '';
  for (const p in params) {
    if (params.hasOwnProperty(p)) {
      if (_encodedStr.length > 0) _encodedStr += '&';
    }
    _encodedStr += encodeURI(p + '=' + params[p]);
  }

  return _encodedStr;
}
export { param };

/**
 * returns formatted url with encoded characters
 * @memberOf Common.Utils.String
 * @param baseUrl : {string} The base url to format (printf like) with the encoded addition.
 * @param toEncode : {string} the string to encode before adding to the base url.
 * @returns {string}
 */

// only takes one argument to encode right now, but can easily be updated to support any number of args to encode

function formatUrl(baseUrl, toEncode) {
  const encoded = encodeURIComponent(toEncode);
  return util.format(baseUrl, encoded);
}
export { formatUrl };

const linkifyText = (network, content, data, tags) => {
  if (network === 'ugc') {
    if (_includes(tags, 'instagram')) {
      network = 'instagram';
    }
    if (_includes(tags, 'twitter')) {
      network = 'twitter';
    }
    if (_includes(tags, 'facebook')) {
      network = 'facebook';
    }
  }

  if (network !== 'twitter' && network !== 'instagram' && network !== 'facebook') return content;
  // Array of urls, hashtags and mentions with their indices
  // Ex. [{ hashtag: "koala", indices: [0,6] }, { url: fb.me/2aabe, indices: [27,42], { screenName: Etsy, indices: [47,52] }]
  const entities = TwitterText.extractEntitiesWithIndices(content);

  // Create a map of the displayedUrls passed in. Ex. { 'google.com': https://t.co/qiza }
  const urlMap = createUrlMap(data);

  const entitiesToLink = _.filter(entities, (entity) => {
    return !!entity.hashtag || !!entity.screenName || !!entity.url;
  });

  // If there are no entities, display the text as is
  if (entitiesToLink.length === 0) return content;

  let htmlString = '',
    endOfLastEntity = 0;
  _.each(entitiesToLink, (entity, idx) => {
    const type = (entity.screenName && 'screenName') || (entity.hashtag && 'hashtag') || (entity.url && 'url');
    const value = entity[type];
    htmlString += content.slice(endOfLastEntity, entity.indices[0]);
    htmlString += anchorMethods[type](network, value, urlMap[value]);
    endOfLastEntity = entity.indices[1];
  });

  htmlString += content.slice(endOfLastEntity);
  return htmlString;
};

// Support methods for linkTweet
const anchorMethods = {
  hashtag: (network, hashtag) =>
    `<a href="${formatHashtagUrl(hashtag, network)}?src=hash" target="_blank">#${hashtag}</a>`,
  screenName: (network, screenName) =>
    `<a href="${formatScreenNameUrl(screenName, network)}" target="_blank">@${screenName}</a>`,
  url: (network, url, linkUrl) => {
    const urlRegex = /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?.*$/;
    // Makes the url protocol relative if there is not supplied linkUrl and it isn't a valid url
    if (!url.match(urlRegex) && !linkUrl) {
      linkUrl = `//${url}`;
    }
    // There is some content that was consumed by us that still has the protocol on the URL.
    // This is to clear it in those instances.
    if (url.match(urlRegex)) {
      if (!linkUrl) {
        linkUrl = url;
      }
      url = url.replace(/^(https?:\/\/)/, '');
      url = url.replace(/^(www.)/, '');
    }
    return `<a href="${linkUrl}" target="_blank">${url}</a>`;
  },
};

const formatHashtagUrl = (hashtag, network) => {
  switch (network) {
    case 'instagram':
      return `${INSTAGRAM_TAG_URL_PREFIX}${hashtag}`;
    case 'twitter':
      return `${TWITTER_TAG_URL_PREFIX}${hashtag}`;
    case 'facebook':
      return `${FACEBOOK_TAG_URL_PREFIX}${hashtag}`;
  }
};

const formatScreenNameUrl = (screenName, network) => {
  switch (network) {
    case 'instagram':
      return `${INSTAGRAM_SCREEN_NAME_URL_PREFIX}${screenName}`;
    case 'twitter':
      return `${TWITTER_SCREEN_NAME_URL_PREFIX}${screenName}`;
      return;
  }
};

const createUrlMap = (displayedUrls) => {
  return _.reduce(
    displayedUrls,
    (map, url) => {
      const ellipsisFreeUrl = url.displayedUrl.replace(/\u2026$|\.{3}$/, '');
      map[ellipsisFreeUrl] = url.linkUrl;
      return map;
    },
    {}
  );
};

const _test = {
  INSTAGRAM_SCREEN_NAME_URL_PREFIX,
  INSTAGRAM_TAG_URL_PREFIX,
  TWITTER_TAG_URL_PREFIX,
  TWITTER_SCREEN_NAME_URL_PREFIX,
  FACEBOOK_TAG_URL_PREFIX,
};

export { linkifyText, _test };
